import React, { useState } from "react";
import { publishStandardCourse } from "../../../api/backend";
import { ConfirmDialog, Notice, Panel, StatusChip } from "../ui/AdminUi";
import { I } from "../ui/icons";
import { errorText } from "./common";

const HINT = { fontSize: 12.5, color: "var(--ga-sub)", marginTop: 6 };

/**
 * Review and publish. The checklist lists what blocks publishing. Publishing needs a change note, because centers see
 * it when they pull the version, and typing the version number to confirm. A published version never changes after this.
 */
export default function PublishPanel({ draft, courseId, checklist, onRecheck, onPublished }) {
  const [changeNote, setChangeNote] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState(null);

  const version = draft.version.versionNumber;
  const note = changeNote.trim();
  const ready = Boolean(checklist?.ready);
  const canPublish = ready && note.length > 0 && !publishing;

  const publish = async () => {
    setPublishing(true);
    setError(null);
    try {
      await publishStandardCourse(courseId, note);
      onPublished();
    } catch (e) {
      setError(errorText(e, "The course could not be published."));
      onRecheck();
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div data-testid="publish-panel">
      {error && (
        <div style={{ marginBottom: 16 }}>
          <Notice kind="bad">
            {error}{" "}
            <button type="button" className="ib" aria-label="Close" style={{ width: 24, height: 24, verticalAlign: "middle" }} onClick={() => setError(null)}><I.x s={14} /></button>
          </Notice>
        </div>
      )}

      <Panel title="Review and publish" subtitle={`Version ${version} is a draft that only you can see.`}
        actions={checklist ? (ready ? <StatusChip status="OK" label="Ready to publish" /> : <StatusChip status="Failed" label={`${checklist.problems.length} blocking`} />) : null}>
        <div className="panel-b" style={{ display: "grid", gap: 16 }}>
          {!checklist ? (
            <div className="sub">Checking the draft…</div>
          ) : ready ? (
            <Notice kind="ok">Everything the centers need is in place.</Notice>
          ) : (
            <Notice kind="warn">
              <div style={{ fontWeight: 600, marginBottom: 4 }}>Fix these before publishing:</div>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {checklist.problems.map((p) => <li key={p}>{p}</li>)}
              </ul>
            </Notice>
          )}

          <div style={{ display: "grid", gap: 6 }}>
            <label className="field">What changed in this version
              <textarea className="ta" rows={2} required aria-required="true" value={changeNote} onChange={(e) => setChangeNote(e.target.value)} />
            </label>
            <span className="sub" style={{ fontSize: 12.5 }}>Required. Centers see this when they pull the version.</span>
          </div>

          <Notice kind="neutral">
            Publishing freezes version {version}: its sessions, skill sheets, tests and documents can no longer change, and it becomes available
            to training centers that offer this category. Classes already created keep the version they started with.
          </Notice>

          <div>
            <button type="button" className="b pri" onClick={() => setConfirming(true)} disabled={!canPublish}>Publish version {version}</button>
            {!ready && checklist && <div style={HINT}>Fix the blocking items to publish.</div>}
            {ready && note.length === 0 && <div style={HINT}>Add a change note to publish.</div>}
          </div>
        </div>
      </Panel>

      <ConfirmDialog
        open={confirming}
        title={`Publish version ${version}?`}
        confirmLabel={`Publish v${version}`}
        destructive={false}
        typed={`publish v${version}`}
        consequence={`Version ${version} becomes the version training centers pull from now on. It cannot be edited after publishing.`}
        keeps="Classes already created keep the version they started with. Centers choose when to move their Knowledge Bank copy to the new version."
        onClose={() => setConfirming(false)}
        onConfirm={() => { setConfirming(false); publish(); }}
      />
    </div>
  );
}
